import React, { useState } from 'react';
import { Button } from '../../../design-system';

interface ClarifyingQuestion {
  question: string;
  options: string[];
}

interface ClarifyingQuestionsProps {
  questions: ClarifyingQuestion[];
  onSendMessage: (message: string) => void;
  isLastAgentMessage?: boolean;
}

export const ClarifyingQuestions: React.FC<ClarifyingQuestionsProps> = ({
  questions,
  onSendMessage,
  isLastAgentMessage = true,
}) => {
  // Track selected option for each question (by index)
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleOptionSelect = (questionIndex: number, optionIndex: number) => {
    if (isSubmitted) return;
    setSelectedOptions(prev => ({
      ...prev,
      [questionIndex]: optionIndex
    }));
  };

  const hasAnySelection = Object.keys(selectedOptions).length > 0;
  const canSubmit = hasAnySelection && isLastAgentMessage && !isSubmitted;

  const handleSubmit = () => {
    if (!canSubmit) return;

    // Build the response message
    const responses: string[] = [];
    questions.forEach((q, qIndex) => {
      if (selectedOptions[qIndex] !== undefined) {
        const selectedOption = q.options[selectedOptions[qIndex]];
        responses.push(`${q.question}: ${selectedOption}`);
      }
    });

    const message = responses.join('\n');
    setIsSubmitted(true);
    onSendMessage(message);
  };

  return (
    <div className="overflow-hidden rounded-ds-12 border border-ds-100 bg-ds-white" dir="rtl">
      {/* Header */}
      <div className="border-b border-ds-100 bg-ds-navy-soft px-4 py-3">
        <h3 className="flex items-center gap-2 text-[15px] font-bold text-ds-ink">
          <span className="text-lg" aria-hidden="true">؟</span>
          <span>چند تا سوال کوتاه</span>
        </h3>
        <p className="mt-1 text-[11px] leading-5 text-ds-600">
          برای اینکه بهتر کمکت کنیم، لطفاً به سوال‌های زیر جواب بده
        </p>
      </div>

      {/* Questions */}
      <div className="space-y-5 p-4">
        {questions.map((q, qIndex) => (
          <div key={qIndex} className="space-y-2.5">
            {/* Question */}
            <h4 className="flex items-center gap-2 text-[13px] font-bold text-ds-800">
              <span className="grid h-5 w-5 place-items-center rounded-full bg-ds-primary-soft text-[10px] font-bold text-ds-primary-strong">
                {qIndex + 1}
              </span>
              {q.question}
            </h4>

            {/* Options */}
            <div className="grid grid-cols-1 gap-2 pr-7 app-sm:grid-cols-2">
              {q.options.map((option, oIndex) => {
                const isSelected = selectedOptions[qIndex] === oIndex;
                return (
                  <button
                    key={oIndex}
                    onClick={() => handleOptionSelect(qIndex, oIndex)}
                    disabled={isSubmitted}
                    className={`rounded-ds-8 border px-3 py-2.5 text-right text-xs transition-colors ${isSelected ? 'border-ds-primary bg-ds-primary-soft font-bold text-ds-primary-strong' : 'border-ds-200 bg-ds-white text-ds-700 hover:bg-ds-subtle'} ${isSubmitted ? 'cursor-not-allowed opacity-70' : ''}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${isSelected ? 'border-ds-primary bg-ds-white' : 'border-ds-300'}`}>
                        {isSelected && (
                          <span className="h-2 w-2 rounded-full bg-ds-primary" />
                        )}
                      </span>
                      <span className="leading-relaxed">{option}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Submit Button */}
      <div className="border-t border-ds-100 bg-ds-canvas px-4 py-3">
        {isSubmitted ? (
          <div className="flex items-center justify-center gap-2 text-xs text-ds-success">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>جوابت ارسال شد</span>
          </div>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            fullWidth
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            <span>ارسال جواب</span>
          </Button>
        )}
        
        {!isLastAgentMessage && !isSubmitted && hasAnySelection && (
          <p className="mt-2 text-center text-xs text-ds-warning">
            این نظرسنجی تموم شده
          </p>
        )}
      </div>
    </div>
  );
};
